import ObjectID from 'bson-objectid';

import generateSurveyLists from '../../../helpers/generateSurveyLists/generateSurveyLists';
import sortStickyGroups from '../../../helpers/sortStickyGroups/sortStickyGroups';
import { moveQuestionAfterFollowUpGroup } from '../../../helpers/sortMultipleAnswerMCGroupsAndJumps/sortMultipleAnswerMCGroupsAndJumps';

const putQuestionInFollowUpGroup = (
  surveyToModify,
  maMCQuestionId,
  followUpGroup,
  newChoice
) => {
  const newSurveyToModify = surveyToModify;

  // Put questions in the followUpGroup
  const questionChoicesToCheck =
    surveyToModify.questions &&
    surveyToModify.questions[maMCQuestionId] &&
    surveyToModify.questions[maMCQuestionId].choices;

  const choiceIndex = questionChoicesToCheck.findIndex(
    c => c.id === newChoice.id
  );

  questionChoicesToCheck[choiceIndex] = newChoice;

  const sortedGroupFlows = questionChoicesToCheck.reduce((acc, c) => {
    if (c.nextFlow && acc.indexOf(c.nextFlow) === -1) {
      return [...acc, c.nextFlow];
    }

    return acc;
  }, []);

  const groupIndex = surveyToModify.content.groups.findIndex(
    g => g.id === followUpGroup.id
  );

  if (groupIndex >= 0) {
    newSurveyToModify.content.groups[groupIndex].flows = sortedGroupFlows;
  }

  return newSurveyToModify;
};

export const getIfQuestionsPointIntoThisFollowUpQuestion = (
  surveyToModify,
  maMCQuestionId,
  targetQuestionId,
  newChoice
) => {
  const questionChoicesToCheck =
    surveyToModify.questions &&
    surveyToModify.questions[maMCQuestionId] &&
    surveyToModify.questions[maMCQuestionId].choices;

  const choiceIndex = questionChoicesToCheck.findIndex(
    c => c.id === newChoice.id
  );
  questionChoicesToCheck[choiceIndex] = newChoice;

  return questionChoicesToCheck.some(c => c.nextFlow === targetQuestionId);
};

const createNewFollowUpGroup = (
  state,
  surveyToModify,
  newChoice,
  maMCQuestion,
  maMCQuestionIndex
) => {
  // There is no follow-up group, then create it
  const newGroupId = ObjectID().toString();

  const surveyListMAMultipleChoiceQuestion =
    state &&
    state.surveyLists &&
    state.surveyLists.length &&
    state.surveyLists.find(q => q.id === maMCQuestion.id);
  const multipleChoiceQuestionFormattedIndex =
    surveyListMAMultipleChoiceQuestion.formattedGlobalIndex;

  const newGroup = {
    id: newGroupId,
    label: `Follow-up question(s) for Q${multipleChoiceQuestionFormattedIndex}`,
    flows: [newChoice.nextFlow],
    followUp: maMCQuestion.id
  };

  // Add new group
  surveyToModify.content.groups.push(newGroup);

  // Add group id to content flows
  surveyToModify.content.flows.splice(maMCQuestionIndex + 1, 0, newGroupId);

  return surveyToModify.content.groups[
    surveyToModify.content.groups.length - 1
  ];
};

const removeQuestionFromSurveyFlow = (surveyToModify, targetQuestionId) => {
  const newSurveyToModify = surveyToModify;
  newSurveyToModify.content.flows = newSurveyToModify.content.flows.filter(
    f => f !== targetQuestionId
  );
  return newSurveyToModify;
};

export default (
  state,
  flowId,
  choice,
  answerUpdate,
  fullQuestionSurveyUpdate
) => {
  let surveyToModify = JSON.parse(JSON.stringify(state.survey));

  const choiceIndex = surveyToModify.questions[flowId].choices.findIndex(
    c => c.id === choice.id
  );

  const flowQuestion = surveyToModify.questions[flowId];

  const currentChoice = surveyToModify.questions[flowId].choices[choiceIndex];

  const newChoice = {
    ...surveyToModify.questions[flowId].choices[choiceIndex],
    ...choice
  };

  if (fullQuestionSurveyUpdate) {
    const flowIdIndex = surveyToModify.content.flows.findIndex(
      f => f.toString() === flowId.toString()
    );
    const flowAfterId = surveyToModify.content.flows[flowIdIndex + 1];
    let followUpGroup =
      surveyToModify.content.groups &&
      surveyToModify.content.groups.length &&
      surveyToModify.content.groups.find(
        g =>
          g.id === flowAfterId &&
          g.followUp &&
          g.followUp.toString() === flowId.toString()
      );

    // Selected new next Flow
    if (
      currentChoice &&
      newChoice &&
      !currentChoice.nextFlow &&
      newChoice.nextFlow
    ) {
      // If there is no followUp group, create it and add question in group flows
      if (!followUpGroup) {
        followUpGroup = createNewFollowUpGroup(
          state,
          surveyToModify,
          newChoice,
          flowQuestion,
          flowIdIndex
        );
      } else {
        // Put question in followUp group
        surveyToModify = putQuestionInFollowUpGroup(
          surveyToModify,
          flowQuestion.id,
          followUpGroup,
          newChoice
        );
      }

      // Remove question from the survey flows
      surveyToModify = removeQuestionFromSurveyFlow(
        surveyToModify,
        newChoice.nextFlow
      );
    }

    // Changed next flow
    if (
      currentChoice &&
      newChoice &&
      currentChoice.nextFlow &&
      newChoice.nextFlow &&
      currentChoice.nextFlow !== newChoice.nextFlow
    ) {
      // Put old question after followUp group if there are no other questions pointing to this so it has to be in the followUp
      if (
        !getIfQuestionsPointIntoThisFollowUpQuestion(
          surveyToModify,
          flowQuestion.id,
          currentChoice.nextFlow,
          newChoice
        )
      ) {
        // Move old jump question after followUp group
        moveQuestionAfterFollowUpGroup(
          [currentChoice.nextFlow],
          surveyToModify,
          followUpGroup,
          true
        );
      }

      surveyToModify = putQuestionInFollowUpGroup(
        surveyToModify,
        flowQuestion.id,
        followUpGroup,
        newChoice
      );

      // Remove question from the survey flows
      surveyToModify = removeQuestionFromSurveyFlow(
        surveyToModify,
        newChoice.nextFlow
      );
    }

    // Removed next Flow
    if (
      currentChoice &&
      newChoice &&
      currentChoice.nextFlow &&
      !newChoice.nextFlow
    ) {
      // Remove question from followUp group and position it after if there are no other questions pointing to this so it has to be in the followUp
      if (
        !getIfQuestionsPointIntoThisFollowUpQuestion(
          surveyToModify,
          flowQuestion.id,
          currentChoice.nextFlow,
          newChoice
        )
      ) {
        // Move question after followUp group
        moveQuestionAfterFollowUpGroup(
          [currentChoice.nextFlow],
          surveyToModify,
          followUpGroup
        );
      }
    }
  }

  if (choice.nextFlow) {
    // eslint-disable-next-line
    delete newChoice.end;
  }

  if (choice.end) {
    // eslint-disable-next-line
    delete newChoice.nextFlow;
  }

  const choices = [...surveyToModify.questions[flowId].choices];
  choices[choiceIndex] = newChoice;

  let activeFlow = {
    ...state.activeFlow
  };

  if (!activeFlow.id) {
    activeFlow = {
      ...activeFlow,
      ...surveyToModify.questions[flowId]
    };
  }

  activeFlow = {
    ...activeFlow,
    choices
  };

  const modifiedFlow = {
    ...surveyToModify.questions[flowId],
    choices
  };

  let newActiveFlow = { ...activeFlow };
  if (newActiveFlow && flowId && newActiveFlow.id !== flowId) {
    newActiveFlow = {
      // ...newActiveFlow,
      ...surveyToModify.questions[flowId]
    };
  }

  const newState = {
    newState: {
      ...state,
      survey: {
        ...surveyToModify,
        questions: {
          ...surveyToModify.questions,
          [flowId]: modifiedFlow
        }
      },
      activeFlow: newActiveFlow
    }
  };

  if (fullQuestionSurveyUpdate) {
    sortStickyGroups(newState.newState);
  }

  if (!answerUpdate) {
    newState.newState.surveyLists = generateSurveyLists(
      newState.newState.survey.content,
      newState.newState.survey.questions
    );
  }

  return newState;
};
